import { useMemo, useCallback } from "react";
import { ASESORIA_EXTERNA_USD_2026, camposCostosFijos } from "../constants";

/**
 * Hook que encapsula los cálculos de Costos Fijos:
 * - Proyecciones con ventana de 12 meses continuos
 * - Valores reales vs proyectados por país
 * - Totales mensuales Chile/Perú/genérico/combinado
 * - YTD de costos fijos
 */
export function useCostosFijosCalcs({
  costosFijosData,
  costosFijosDataAnoAnterior,
  costosFijosChile,
  costosFijosPeru,
  rawCostosFijos,
  rawCostosFijosChile,
  rawCostosFijosPeru,
  selectedYear,
  monthsToShow,
  modoCombinado,
  pais,
  getDolarDia10,
  dolarMaps,
  dolarMensualChile,
  dolarMensualPeru,
  rrhhMensualConBeneficios,
  rrhhMensualChile,
  rrhhMensualPeru,
  rrhhMensualConBeneficiosAnoAnterior,
  dolarFallback,
  costosFijosChileAnoAnterior,
  costosFijosPeruAnoAnterior,
}) {
  // ---------------------------------------------------------------------------
  // Proyecciones de costos fijos (ventana 12 meses continuos)
  // ---------------------------------------------------------------------------
  const getProjectedCostoFijo = useCallback(
    (campo, monthIndex) => {
      // Caso especial: Asesoría externa en 2026 → US$ 3.000 × tipo de cambio día 10
      if (campo === "asesoria_externa" && selectedYear === 2026) {
        const tipoCambio = getDolarDia10(monthIndex, dolarMaps.actual, pais === "Chile" ? 950 : 3.75);
        return Math.round(ASESORIA_EXTERNA_USD_2026 * tipoCambio);
      }
      // Ventana de 12 meses continuos: año actual + año anterior
      const valores12Meses = [];
      for (let i = 0; i < monthIndex; i++) {
        const valor = costosFijosData[i]?.[campo];
        if (valor > 0) valores12Meses.push(valor);
      }
      const mesesFaltantes = 12 - valores12Meses.length;
      if (mesesFaltantes > 0 && costosFijosDataAnoAnterior.length > 0) {
        for (let i = 11; i >= 0 && valores12Meses.length < 12; i--) {
          const valor = costosFijosDataAnoAnterior[i]?.[campo];
          if (valor > 0) valores12Meses.push(valor);
        }
      }
      if (valores12Meses.length === 0) return 0;
      return Math.round(valores12Meses.reduce((a, b) => a + b, 0) / valores12Meses.length);
    },
    [costosFijosData, costosFijosDataAnoAnterior, selectedYear, getDolarDia10, dolarMaps.actual, pais]
  );

  const getProjectedCostoFijoChile = useCallback(
    (campo, monthIndex) => {
      if (campo === "asesoria_externa" && selectedYear === 2026) {
        const tipoCambio = getDolarDia10(monthIndex, dolarMaps.chile, 950);
        return Math.round(ASESORIA_EXTERNA_USD_2026 * tipoCambio);
      }
      // Ventana de 12 meses continuos: año actual + año anterior
      const valores12Meses = [];
      for (let i = 0; i < monthIndex; i++) {
        const valor = costosFijosChile[i]?.[campo];
        if (valor > 0) valores12Meses.push(valor);
      }
      if (valores12Meses.length < 12 && costosFijosChileAnoAnterior?.length > 0) {
        for (let i = 11; i >= 0 && valores12Meses.length < 12; i--) {
          const valor = costosFijosChileAnoAnterior[i]?.[campo];
          if (valor > 0) valores12Meses.push(valor);
        }
      }
      if (valores12Meses.length === 0) return 0;
      return Math.round(valores12Meses.reduce((a, b) => a + b, 0) / valores12Meses.length);
    },
    [costosFijosChile, costosFijosChileAnoAnterior, selectedYear, getDolarDia10, dolarMaps.chile]
  );

  const getProjectedCostoFijoPeru = useCallback(
    (campo, monthIndex) => {
      if (campo === "asesoria_externa" && selectedYear === 2026) {
        const tipoCambio = getDolarDia10(monthIndex, dolarMaps.peru, 3.75);
        return Math.round(ASESORIA_EXTERNA_USD_2026 * tipoCambio);
      }
      // Ventana de 12 meses continuos: año actual + año anterior
      const valores12Meses = [];
      for (let i = 0; i < monthIndex; i++) {
        const valor = costosFijosPeru[i]?.[campo];
        if (valor > 0) valores12Meses.push(valor);
      }
      if (valores12Meses.length < 12 && costosFijosPeruAnoAnterior?.length > 0) {
        for (let i = 11; i >= 0 && valores12Meses.length < 12; i--) {
          const valor = costosFijosPeruAnoAnterior[i]?.[campo];
          if (valor > 0) valores12Meses.push(valor);
        }
      }
      if (valores12Meses.length === 0) return 0;
      return Math.round(valores12Meses.reduce((a, b) => a + b, 0) / valores12Meses.length);
    },
    [costosFijosPeru, costosFijosPeruAnoAnterior, selectedYear, getDolarDia10, dolarMaps.peru]
  );

  // ---------------------------------------------------------------------------
  // Obtener valor real o proyectado por país
  // ---------------------------------------------------------------------------
  const getCostoFijoValor = useCallback(
    (campo, monthIndex) => {
      const registroExiste = rawCostosFijos?.some(
        (r) => Number(r.mes) === monthIndex + 1 && r[campo] !== undefined && r[campo] !== null
      );
      const valorReal = costosFijosData[monthIndex]?.[campo];
      if (monthIndex < monthsToShow && !registroExiste) {
        return getProjectedCostoFijo(campo, monthIndex);
      }
      return valorReal ?? 0;
    },
    [rawCostosFijos, costosFijosData, monthsToShow, getProjectedCostoFijo]
  );

  const getCostoFijoValorChile = useCallback(
    (campo, monthIndex) => {
      const registroExiste = rawCostosFijosChile?.some(
        (r) => Number(r.mes) === monthIndex + 1 && r[campo] !== undefined && r[campo] !== null
      );
      const valorReal = costosFijosChile[monthIndex]?.[campo];
      if (monthIndex < monthsToShow && !registroExiste) {
        return getProjectedCostoFijoChile(campo, monthIndex);
      }
      return valorReal ?? 0;
    },
    [rawCostosFijosChile, costosFijosChile, monthsToShow, getProjectedCostoFijoChile]
  );

  const getCostoFijoValorPeru = useCallback(
    (campo, monthIndex) => {
      const registroExiste = rawCostosFijosPeru?.some(
        (r) => Number(r.mes) === monthIndex + 1 && r[campo] !== undefined && r[campo] !== null
      );
      const valorReal = costosFijosPeru[monthIndex]?.[campo];
      if (monthIndex < monthsToShow && !registroExiste) {
        return getProjectedCostoFijoPeru(campo, monthIndex);
      }
      return valorReal ?? 0;
    },
    [rawCostosFijosPeru, costosFijosPeru, monthsToShow, getProjectedCostoFijoPeru]
  );

  // ---------------------------------------------------------------------------
  // Costos fijos mensuales por país
  // ---------------------------------------------------------------------------
  const costosFijosMensualAnoAnterior = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => {
          const cfAA = costosFijosDataAnoAnterior[i] || {};
          const cfBase = camposCostosFijos.reduce((sum, campo) => {
            const valorReal = cfAA[campo];
            // Si el campo tiene valor real (no null/undefined), usarlo
            if (valorReal !== undefined && valorReal !== null) {
              return sum + (Number(valorReal) || 0);
            }
            // Campo sin dato → proyectar con promedio de meses disponibles
            // (consistente con lógica de getCostoFijoValor para el año actual)
            const valoresDisponibles = [];
            for (let j = 0; j < 12; j++) {
              const v = costosFijosDataAnoAnterior[j]?.[campo];
              if (v !== undefined && v !== null && Number(v) > 0) {
                valoresDisponibles.push(Number(v));
              }
            }
            if (valoresDisponibles.length === 0) return sum;
            return sum + Math.round(valoresDisponibles.reduce((a, b) => a + b, 0) / valoresDisponibles.length);
          }, 0);
          const rrhh = rrhhMensualConBeneficiosAnoAnterior[i] || 0;
          return cfBase + rrhh;
        }),
    [costosFijosDataAnoAnterior, rrhhMensualConBeneficiosAnoAnterior]
  );

  const costosFijosMensualChile = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => {
          const cfBase = camposCostosFijos.reduce((sum, campo) => sum + (getCostoFijoValorChile(campo, i) ?? 0), 0);
          const rrhh = i < monthsToShow ? rrhhMensualChile[i] || 0 : 0;
          return cfBase + rrhh;
        }),
    [getCostoFijoValorChile, rrhhMensualChile, monthsToShow]
  );

  const costosFijosMensualPeru = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => {
          const cfBase = camposCostosFijos.reduce((sum, campo) => sum + (getCostoFijoValorPeru(campo, i) ?? 0), 0);
          const rrhh = i < monthsToShow ? rrhhMensualPeru[i] || 0 : 0;
          return cfBase + rrhh;
        }),
    [getCostoFijoValorPeru, rrhhMensualPeru, monthsToShow]
  );

  // ---------------------------------------------------------------------------
  // sumarCostosFijosMes (genérico) + YTD
  // ---------------------------------------------------------------------------
  const sumarCostosFijosMes = useCallback(
    (monthIndex) =>
      camposCostosFijos.reduce((sum, campo) => sum + (getCostoFijoValor(campo, monthIndex) ?? 0), 0) +
      (monthIndex < monthsToShow ? rrhhMensualConBeneficios[monthIndex] : 0),
    [getCostoFijoValor, rrhhMensualConBeneficios, monthsToShow]
  );

  const costosFijosYTD = useMemo(() => {
    let total = 0;
    for (let i = 0; i < monthsToShow; i++) {
      total += sumarCostosFijosMes(i);
    }
    return total;
  }, [monthsToShow, sumarCostosFijosMes]);

  // ---------------------------------------------------------------------------
  // Combinados en USD
  // ---------------------------------------------------------------------------
  const costosFijosMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;

    const resultado = Array(12).fill(0);

    for (let i = 0; i < 12; i++) {
      const cfChileTotal = costosFijosMensualChile[i] || 0;
      const cfPeruTotal = costosFijosMensualPeru[i] || 0;

      const chileUSD = cfChileTotal / (dolarMensualChile[i] || dolarFallback.Chile);
      const peruUSD = cfPeruTotal / (dolarMensualPeru[i] || dolarFallback["Perú"]);

      resultado[i] = chileUSD + peruUSD;
    }

    return resultado;
  }, [modoCombinado, costosFijosMensualChile, costosFijosMensualPeru, dolarMensualChile, dolarMensualPeru]);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    // Proyecciones (expuestas para JSX de tabla)
    getProjectedCostoFijo,
    // Valores reales/proyectados
    getCostoFijoValor,
    getCostoFijoValorChile,
    getCostoFijoValorPeru,
    // Mensuales
    costosFijosMensualAnoAnterior,
    costosFijosMensualChile,
    costosFijosMensualPeru,
    // Totales
    sumarCostosFijosMes,
    costosFijosYTD,
    costosFijosMensualesCombinados,
  };
}
